/**
 * Post CAP Home Check: same flow as checkout site survey — GET appointments (with Pre-Permit
 * `specialWorktype`), GET slots by ready-row id, create/cancel/reschedule via prospect service.
 */
import { useCallback, useState } from "react";
import {
  CancelSiteSurveyAppointmentRequestData,
  SiteSurveySlotRequestData,
} from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { SITE_SURVEY_APPOINTMENTS_SPECIAL_WORKTYPE_PRE_PERMIT_CHECK } from "../constants/postCapHomeCheck";
import {
  useSiteSurveyAppointments,
  useSiteSurveySlots,
  buildScheduledSiteSurveyAppointmentFromBookedSlot,
  isScheduledSiteSurveyAppointment,
  mergeBookedSiteSurveyAppointmentIntoCache,
  type SiteSurveySlot,
} from "checkout/api/siteSurvey";
import { useAuthToken } from "hooks/useAuthToken";
import {
  cancelSiteSurveyAppointment,
  createSiteSurveyAppointment,
  rescheduleSiteSurveyAppointment,
} from "services/prospect";

const SITE_SURVEY_BOOK_APPOINTMENT_TYPE = "Site Survey";

type UsePostCapHomeCheckOptions = {
  mutateSow?: () => Promise<unknown>;
};

const usePostCapHomeCheck = (
  prospectId: string | undefined,
  { mutateSow }: UsePostCapHomeCheckOptions = {}
) => {
  const authKey = useAuthToken("HYBRID");
  const {
    siteSurveyReadyToBeScheduled,
    siteSurveyAppointment,
    siteSurveyAppointmentsLoading,
    siteSurveyApointmentError,
    mutateSiteSurveyAppointments,
  } = useSiteSurveyAppointments(prospectId, {
    specialWorktype: SITE_SURVEY_APPOINTMENTS_SPECIAL_WORKTYPE_PRE_PERMIT_CHECK,
  });

  const slotsState = useSiteSurveySlots(
    prospectId || "",
    siteSurveyReadyToBeScheduled?.appointmentId ?? ""
  );

  const [bookingLoading, setBookingLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  const bookSlot = useCallback(
    (slot: SiteSurveySlot) => {
      if (!prospectId || !authKey?.token) {
        const msg = !prospectId
          ? "bookSlot: missing prospectId"
          : "bookSlot: missing hybrid auth token";
        return Promise.reject(new Error(msg));
      }

      setBookingLoading(true);
      const requestObj: SiteSurveySlotRequestData = {
        appointmentId: slot.appointmentId,
        arrivalStartDate: slot.arrivalStartDate,
        arrivalEndDate: slot.arrivalEndDate,
        appointmentType: SITE_SURVEY_BOOK_APPOINTMENT_TYPE,
        isComplexAppointment: false,
      };

      return createSiteSurveyAppointment(authKey.token, prospectId, requestObj)
        .then(async (result) => {
          const scheduledFromBooking = isScheduledSiteSurveyAppointment(result)
            ? result
            : buildScheduledSiteSurveyAppointmentFromBookedSlot(
                slot,
                siteSurveyReadyToBeScheduled,
                prospectId
              );

          await mutateSiteSurveyAppointments(
            (current) =>
              mergeBookedSiteSurveyAppointmentIntoCache(
                current,
                scheduledFromBooking
              ),
            { revalidate: true }
          );
          mutateSow?.();
        })
        .catch((err) => {
          console.error("[PostCapHomeCheck] bookSlot failed", err);
          throw err;
        })
        .finally(() => setBookingLoading(false));
    },
    [
      authKey?.token,
      mutateSiteSurveyAppointments,
      mutateSow,
      prospectId,
      siteSurveyReadyToBeScheduled,
    ]
  );

  const scheduledAppointment = siteSurveyAppointment;

  const cancelAppointment = useCallback(
    (reason: string) => {
      if (!scheduledAppointment || !prospectId || !authKey?.token) {
        const msg = !scheduledAppointment
          ? "cancel: no scheduled appointment in state"
          : !prospectId
          ? "cancel: missing prospectId"
          : "cancel: missing hybrid auth token";
        return Promise.reject(new Error(msg));
      }
      setCancelLoading(true);
      const requestObj = {
        ...scheduledAppointment,
        cancellationReason: reason,
        isComplexAppointment: false,
      } as CancelSiteSurveyAppointmentRequestData;

      return cancelSiteSurveyAppointment(
        authKey.token,
        prospectId,
        scheduledAppointment.appointmentId,
        requestObj
      )
        .then((result) => {
          return mutateSiteSurveyAppointments();
        })
        .catch((err) => {
          console.error("[PostCapHomeCheck] cancel failed", err);
          throw err;
        })
        .finally(() => setCancelLoading(false));
    },
    [
      authKey?.token,
      mutateSiteSurveyAppointments,
      prospectId,
      scheduledAppointment,
    ]
  );

  const rescheduleAppointment = useCallback(() => {
    if (!scheduledAppointment?.appointmentId || !authKey?.token) {
      const msg = !scheduledAppointment?.appointmentId
        ? "reschedule: missing appointment id"
        : "reschedule: missing hybrid auth token";
      return Promise.reject(new Error(msg));
    }
    setRescheduleLoading(true);
    return rescheduleSiteSurveyAppointment(authKey.token, {
      appointmentId: scheduledAppointment.appointmentId,
      rescheduleReason: "Customer Reschedule",
      isComplexAppointment: false,
    })
      .then((result) => {
        return mutateSiteSurveyAppointments();
      })
      .catch((err) => {
        console.error("[PostCapHomeCheck] reschedule failed", err);
        throw err;
      })
      .finally(() => setRescheduleLoading(false));
  }, [
    authKey?.token,
    mutateSiteSurveyAppointments,
    scheduledAppointment?.appointmentId,
  ]);

  return {
    slotBookingParentId: siteSurveyReadyToBeScheduled?.appointmentId,
    scheduledAppointment,
    bookingSnapshotLoading: siteSurveyAppointmentsLoading,
    bookingSnapshotError: siteSurveyApointmentError,
    mutateBookingSnapshot: mutateSiteSurveyAppointments,
    slots: slotsState.data,
    slotsLoading: slotsState.isLoading,
    slotsError: slotsState.error,
    retrySlots: slotsState.retry,
    bookingLoading,
    cancelLoading,
    rescheduleLoading,
    bookSlot,
    cancelAppointment,
    rescheduleAppointment,
  };
};

export { usePostCapHomeCheck };
export type { UsePostCapHomeCheckOptions };
